import { forwardRef } from 'react';
import * as PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import InputError from '../InputError';
import styles from './Input.module.scss';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const cln = classnames.bind(styles);

/**
 * This is a helper Input component which handles three things for you: label, input field and errors displaying.
 */
const Input = /*#__PURE__*/forwardRef(function Input({
  containerClassName,
  className,
  size = 'big',
  icon,
  iconPosition = 'left',
  type = 'text',
  onMouseEnter,
  onMouseLeave,
  error,
  disabled = false,
  highlighted = false,
  ...rest
}, ref) {
  const inputWrapperClass = cln('InputWrapper', `InputWrapper--${iconPosition}Icon`, {
    [`InputWrapper--${size}`]: size
  });
  const inputClass = cln(className, 'Input', `Input--${type}`, {
    [`Input--${size}`]: size,
    'Input--error': error,
    'Input--disabled': disabled,
    'Input--highlighted': highlighted
  });
  let NodeType;
  switch (type) {
    case 'text':
      NodeType = 'input';
      break;
    case 'textarea':
      NodeType = 'textarea';
      break;
    default:
      NodeType = 'input';
  }
  const input = /*#__PURE__*/_jsx(NodeType, {
    ref: ref,
    "data-testid": "ru-input",
    className: inputClass,
    type: type,
    disabled: disabled,
    ...rest
  });
  return /*#__PURE__*/_jsxs("span", {
    className: containerClassName,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    children: [icon ? /*#__PURE__*/_jsxs("span", {
      "data-testid": "rc-input-wrapper",
      className: inputWrapperClass,
      children: [input, icon]
    }) : input, error && /*#__PURE__*/_jsx(InputError, {
      children: error
    })]
  });
});
Input.propTypes = {
  /** On of predefined sizes */
  size: PropTypes.oneOf(['big', 'small', 'compact']),
  /** Icon position */
  iconPosition: PropTypes.oneOf(['left', 'right']),
  /** Input type */
  type: PropTypes.oneOf(['text', 'textarea', 'password', 'email', 'number']),
  /** @ignore */
  name: PropTypes.string,
  /** Icon component - will be placed on the left side of the input */
  icon: PropTypes.node,
  /** Error string which should be displayed below the input field. */
  error: PropTypes.string,
  /** Custom input class */
  className: PropTypes.string,
  /** Custom input container class  */
  containerClassName: PropTypes.string,
  /** Whether or not the component should be highlighted */
  highlighted: PropTypes.bool,
  /** @ignore */
  disabled: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
};
export default Input;